import React, { useMemo } from "react";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Zap,
         ArrowRight, Target, ChevronRight } from "lucide-react";

// ── Pure computation functions (deterministic, no API calls) ──────────────
function computeInsights(dash, trend, initiatives) {
  if (!dash) return null;
  const { emissions, primary_target, reporting_year } = dash;
  const assets = emissions?.assets || [];
  const byCategory = emissions?.by_category || {};
  const total = emissions?.total_tco2e || 0;
  const trendArr = trend?.trend || [];

  // ── TOP 3 EMISSION DRIVERS ─────────────────────────────────────────────
  const drivers = Object.entries(byCategory)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([cat, tco2e], idx) => ({
      rank: idx + 1,
      category: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1).replace(/_/g, " "),
      tco2e: parseFloat(tco2e.toFixed(2)),
      pct: total > 0 ? parseFloat((tco2e / total * 100).toFixed(1)) : 0,
    }));

  // Top emitting asset
  const topAsset = [...assets]
    .filter(a => a.total_tco2e != null)
    .sort((a, b) => (b.total_tco2e || 0) - (a.total_tco2e || 0))[0];

  // Growing assets (compare 2024 trend if available)
  const latestYoY = trendArr.length >= 2
    ? trendArr[trendArr.length - 1]?.yoy_delta_pct
    : null;

  // ── TOP 3 RISKS ────────────────────────────────────────────────────────
  const risks = [];

  // 1. Cape Town-type growth offsetting gains
  const growingAssets = assets.filter(a =>
    a.total_tco2e != null && a.pct_of_total > 25 &&
    a.benchmark_position === "worse_than_sector"
  );
  if (growingAssets.length > 0) {
    risks.push({
      severity: "high",
      title: `${growingAssets[0].asset_name} Growth Offsetting Gains`,
      detail: `${growingAssets[0].asset_name} accounts for ${growingAssets[0].pct_of_total?.toFixed(0)}% of total emissions and is above sector average intensity. Capacity growth is counteracting reductions elsewhere.`,
    });
  }

  // 2. Off target glide path
  if (primary_target && !primary_target.on_track && primary_target.gap_tco2e > 0) {
    risks.push({
      severity: "high",
      title: `${Math.abs(primary_target.gap_tco2e?.toFixed(1))} tCO₂e Above Target Glide Path`,
      detail: `${primary_target.name} requires a linear decline but emissions are ${primary_target.gap_tco2e?.toFixed(1)} tCO₂e above the ${reporting_year} trajectory. At current pace, target will be missed.`,
    });
  }

  // 3. Flat/rising trend
  if (latestYoY !== null && latestYoY > -2) {
    const dir = latestYoY > 0 ? "rising" : "near-flat";
    risks.push({
      severity: latestYoY > 1 ? "high" : "medium",
      title: `Emissions Trend ${latestYoY > 0 ? "Rising" : "Flat"} (${latestYoY?.toFixed(1)}% YoY)`,
      detail: `Target requires approximately 5% annual reduction. Current trajectory is ${dir}. Without immediate action pace will increase the cumulative gap.`,
    });
  }

  // 4. Electricity concentration
  if (drivers[0]?.category === "electricity" && (drivers[0]?.pct || 0) > 55) {
    risks.push({
      severity: "medium",
      title: `High Grid Electricity Concentration (${drivers[0].pct}%)`,
      detail: `Over half of emissions from Scope 2 grid electricity. Creates concentration risk and dependency on national grid decarbonisation timeline.`,
    });
  }

  // 5. Coverage (if any)
  if (emissions?.coverage?.pct < 100) {
    const uncovered = emissions.coverage.total - emissions.coverage.covered;
    risks.push({
      severity: "low",
      title: `${uncovered} Asset${uncovered > 1 ? "s" : ""} Without Current Measurement`,
      detail: `Corporate emissions may be understated. Full coverage required for credible institutional disclosure.`,
    });
  }

  // ── TOP 3 RECOMMENDED ACTIONS ─────────────────────────────────────────
  const actions = [];
  const sortedInitiatives = [...(initiatives || [])]
    .filter(i => ["planned", "in_progress"].includes(i.status))
    .sort((a, b) => (b.expected_reduction_tco2e || 0) - (a.expected_reduction_tco2e || 0));

  sortedInitiatives.slice(0, 2).forEach((init, idx) => {
    const conf = init.confidence_level;
    const confLabel = conf === "high" ? "High confidence" : conf === "medium" ? "Medium confidence" : "Low confidence";
    actions.push({
      priority: idx + 1,
      action: init.name,
      impact: `${init.expected_reduction_tco2e?.toFixed(1)} tCO₂e/yr reduction`,
      timeline: `Target: ${init.target_completion_year}`,
      confidence: conf,
      confidenceLabel: confLabel,
      gapContribution: init.gap_contribution_pct,
      status: init.status,
    });
  });

  // Add an annual reassessment action if pace is off
  if (actions.length < 3) {
    actions.push({
      priority: actions.length + 1,
      action: "Accelerate Cape Town Facility Transition",
      impact: "Addresses highest-growth emission source",
      timeline: "Prioritise in next budget cycle",
      confidence: "medium",
      confidenceLabel: "Medium confidence",
      gapContribution: null,
      status: "recommended",
    });
  }

  // ── ON-TRACK SUMMARY ──────────────────────────────────────────────────
  const onTrack = primary_target?.on_track;
  const gap = primary_target?.gap_tco2e;
  const targetName = primary_target?.name || "No target set";
  const targetYear = primary_target?.target_year;
  const requiredAnnualPct = targetYear && reporting_year
    ? parseFloat((primary_target?.target_reduction_pct / (targetYear - (primary_target?.base_year || 2021)) * 100).toFixed(1))
    : null;

  let summary = "";
  if (!primary_target) {
    summary = "No corporate target set. Set a target to track progress and identify gaps.";
  } else if (onTrack) {
    summary = `On track for ${targetName}. Continue current trajectory and accelerate high-confidence initiatives.`;
  } else {
    const yearsLeft = targetYear ? targetYear - reporting_year : null;
    summary = `Off track: ${gap?.toFixed(1)} tCO₂e above the ${reporting_year} glide path for ${targetName}. `
      + (yearsLeft ? `${yearsLeft} years remaining. ` : "")
      + `Implementing the top 2 initiatives would close ${(sortedInitiatives.slice(0, 2).reduce((s, i) => s + (i.gap_contribution_pct || 0), 0)).toFixed(0)}% of the annual gap.`;
  }

  return { drivers, risks: risks.slice(0, 3), actions: actions.slice(0, 3), onTrack, gap, summary, targetName };
}

// ── Sub-components ────────────────────────────────────────────────────────
const SEVERITY_STYLES = {
  high:   { cls: "bg-red-50 border-red-200",   dot: "bg-red-500",    text: "text-red-700" },
  medium: { cls: "bg-amber-50 border-amber-200", dot: "bg-amber-500", text: "text-amber-700" },
  low:    { cls: "bg-slate-50 border-slate-200", dot: "bg-slate-400", text: "text-slate-600" },
};

const CONF_STYLES = {
  high:   { cls: "bg-emerald-50 text-emerald-700", label: "High confidence" },
  medium: { cls: "bg-blue-50 text-blue-700",       label: "Medium confidence" },
  low:    { cls: "bg-amber-50 text-amber-700",      label: "Low confidence" },
};

const CAT_COLORS = {
  electricity: "bg-blue-500", fuel: "bg-amber-500", transport: "bg-violet-500",
  waste: "bg-emerald-500", materials: "bg-pink-500", other: "bg-slate-400",
};

// ── Main Component ────────────────────────────────────────────────────────
export default function CorporateExecutiveInsights({ dashData, trendData, initiatives }) {
  const insights = useMemo(
    () => computeInsights(dashData, trendData, initiatives),
    [dashData, trendData, initiatives]
  );

  if (!insights) return null;
  const { drivers, risks, actions, onTrack, gap, summary, targetName } = insights;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="executive-insights">
      {/* Header */}
      <div className={`px-5 py-3 flex items-center gap-3 ${onTrack ? "bg-emerald-50 border-b border-emerald-200" : "bg-amber-50 border-b border-amber-200"}`}>
        {onTrack
          ? <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
          : <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" />}
        <div className="flex-1">
          <p className={`text-xs font-bold uppercase tracking-wider ${onTrack ? "text-emerald-800" : "text-amber-800"}`}>
            {onTrack ? "On Track" : "Off Track"} — {targetName}
          </p>
          <p className={`text-xs mt-0.5 leading-relaxed ${onTrack ? "text-emerald-700" : "text-amber-700"}`}>
            {summary}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100">

        {/* TOP 3 DRIVERS */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={13} className="text-[#059669]" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Top 3 Emission Drivers</p>
          </div>
          <div className="space-y-2.5">
            {drivers.map(d => (
              <div key={d.category}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-sm ${CAT_COLORS[d.category] || "bg-slate-400"}`} />
                    <span className="text-xs font-medium text-[#0F172A]">#{d.rank} {d.label}</span>
                  </div>
                  <span className="text-xs tabular-nums font-bold text-[#0F172A]">{d.tco2e} t <span className="text-slate-400 font-normal">({d.pct}%)</span></span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${CAT_COLORS[d.category] || "bg-slate-400"}`}
                       style={{ width: `${d.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TOP 3 RISKS */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={13} className="text-amber-500" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Top 3 Risks to Target</p>
          </div>
          <div className="space-y-2">
            {risks.map((r, i) => {
              const s = SEVERITY_STYLES[r.severity] || SEVERITY_STYLES.low;
              return (
                <div key={i} className={`p-2.5 rounded-lg border text-xs ${s.cls}`}>
                  <div className="flex items-start gap-1.5 mb-0.5">
                    <div className={`w-2 h-2 rounded-full mt-0.5 flex-shrink-0 ${s.dot}`} />
                    <p className={`font-semibold leading-tight ${s.text}`}>{r.title}</p>
                  </div>
                  <p className="text-slate-500 leading-relaxed pl-3.5 line-clamp-2">{r.detail}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* TOP 3 ACTIONS */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={13} className="text-[#059669]" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Top 3 Recommended Actions</p>
          </div>
          <div className="space-y-2">
            {actions.map((a, i) => {
              const cs = CONF_STYLES[a.confidence] || CONF_STYLES.medium;
              return (
                <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 border border-slate-100 rounded-lg">
                  <div className="w-5 h-5 bg-[#0F172A] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#0F172A] leading-tight">{a.action}</p>
                    <p className="text-xs text-[#059669] font-medium mt-0.5">{a.impact}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-slate-400">{a.timeline}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${cs.cls}`}>{cs.label}</span>
                      {a.gapContribution && (
                        <span className="text-xs text-[#059669] font-medium">
                          {a.gapContribution.toFixed(0)}% gap closure
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
